import type {
  CasoDaFila,
  Investigacao,
  MovimentoDoExtrato,
  TipoDoMovimento,
} from '../../auditoria';

/**
 * RESUMO DA DIVERGÊNCIA na Conferência da Prateleira (Carlos, 14/10/2026): ao abrir o produto, o
 * conferente vê o HOS de agora, o HOS da hora da contagem, o que o balconista declarou e a
 * diferença que abriu o caso — sem abrir outra tela.
 *
 * Regras (nada inventado):
 *  - HOS agora: o saldo ao vivo (investigação) quando chega; antes disso, o espelho com a hora em
 *    que foi visto. Sem nenhum dos dois: indisponível — nunca zero.
 *  - HOS na contagem: a FOTO congelada na abertura do caso. Nunca é trocada pelo saldo de agora;
 *    sem foto, indisponível. Lida na geração do Giro (não na contagem): dito com todas as letras.
 *  - Estoque do balconista: o Giro não pede ao balconista a contagem do estoque inteiro, então é
 *    "Não informado". O que ele achou na reposição aparece na diferença, nunca como estoque.
 *  - Por que o HOS mudou: só o que o RAZÃO do HOS mostra depois da contagem. Sem razão, não há
 *    explicação — a tela manda consultar as movimentações.
 */

export type FonteDoHosAgora = 'AO_VIVO' | 'ESPELHO';
export type FonteDaFoto = 'CONTAGEM' | 'GERACAO' | 'SEM_LEITURA';
export type TipoDaDiferenca = 'REPOSICAO' | 'INVENTARIO';

export interface ResumoDaConferencia {
  readonly hosAgora: {
    readonly valor: number | null;
    readonly em: string | null;
    readonly fonte: FonteDoHosAgora | null;
  };
  readonly hosNaContagem: {
    readonly valor: number | null;
    readonly em: string | null;
    readonly fonte: FonteDaFoto | null;
  };
  readonly prateleira: {
    readonly valor: number | null;
    readonly por: string | null;
    readonly em: string | null;
  };
  /** O Giro não registra a contagem do estoque inteiro pelo balconista. */
  readonly estoque: { readonly valor: null };
  readonly diferenca: {
    readonly tipo: TipoDaDiferenca;
    /** Com sinal: −12 = faltaram 12 na reposição. */
    readonly qtd: number;
    readonly titulo: string;
    readonly rotulo: string;
    readonly frase: string;
  };
  /** A diferença do estoque físico inteiro, só quando a conciliação tem dados para isso. */
  readonly fisicoTotal: string | null;
  /** O HOS mudou entre a contagem e agora (null = não mudou ou não dá para comparar). */
  readonly mudancaHos: {
    readonly delta: number;
    readonly frase: string;
    /** null = ainda buscando as movimentações. */
    readonly explicacao: string | null;
  } | null;
}

const un = (n: number) => `${String(n)} ${n === 1 ? 'unidade' : 'unidades'}`;
const sinal = (n: number) => (n < 0 ? '−' : n > 0 ? '+' : '');
export const comSinalUn = (n: number) => `${sinal(n)}${un(Math.abs(n))}`;

/** O que o balconista declarou e o sistema pediu → a diferença que abriu o caso. */
export function diferencaDoCaso(c: CasoDaFila): ResumoDaConferencia['diferenca'] {
  if (c.deEstoque) {
    // Caso de estoque: `solicitado` = o que a prateleira mostrou; `encontrado` = o saldo do HOS.
    const qtd = c.solicitado - c.encontrado;
    return {
      tipo: 'INVENTARIO',
      qtd,
      titulo: `${comSinalUn(qtd)} acima do HOS`,
      rotulo: 'Diferença de inventário',
      frase: `A prateleira mostrou ${un(c.solicitado)}, mais do que o HOS dizia ter (${String(c.encontrado)}). É uma pergunta sobre o saldo, não sobre quem contou.`,
    };
  }
  const qtd = c.encontrado - c.solicitado;
  const rotulo = 'Reposição incompleta — não é perda confirmada';
  if (qtd === 0)
    return {
      tipo: 'REPOSICAO',
      qtd,
      titulo: 'Sem diferença na reposição',
      rotulo: 'Reposição',
      frase: `O balconista encontrou ${c.solicitado === 1 ? 'a unidade pedida' : `as ${un(c.solicitado)} pedidas`} para repor.`,
    };
  if (qtd > 0)
    return {
      tipo: 'REPOSICAO',
      qtd,
      titulo: `${comSinalUn(qtd)} na reposição`,
      rotulo: 'Reposição',
      frase: `O sistema pediu para repor ${un(c.solicitado)} e o balconista informou que encontrou ${un(c.encontrado)}.`,
    };
  return {
    tipo: 'REPOSICAO',
    qtd,
    titulo: `${comSinalUn(qtd)} na reposição`,
    rotulo,
    frase:
      c.encontrado === 0
        ? `O sistema pediu para repor ${un(c.solicitado)}, mas o balconista informou que não encontrou nenhuma.`
        : `O sistema pediu para repor ${un(c.solicitado)}, mas o balconista informou que encontrou ${un(c.encontrado)}.`,
  };
}

/** Movimentos que mexem no saldo (sem reposição interna, nota em conferência e cancelados). */
const contaNoSaldo = (m: MovimentoDoExtrato) =>
  m.conta && !m.cancelado && m.tipo !== 'REPOSICAO_INTERNA' && m.tipo !== 'NOTA_EM_CONFERENCIA';

const NOME: Readonly<Record<TipoDoMovimento, string>> = {
  VENDA: 'venda',
  DEVOLUCAO: 'devolução de cliente',
  ENTRADA_NF: 'nota fiscal',
  AJUSTE: 'ajuste',
  CANCELAMENTO: 'cancelamento',
  OUTRO: 'outro movimento',
  REPOSICAO_INTERNA: 'reposição interna',
  NOTA_EM_CONFERENCIA: 'nota em conferência',
};

/** "3 unidades de saída por venda e 6 unidades de entrada por nota fiscal" — só o que o razão diz. */
export function explicarMovimentos(movimentos: readonly MovimentoDoExtrato[]): {
  readonly texto: string | null;
  readonly soma: number;
} {
  const grupos = new Map<string, { tipo: TipoDoMovimento; entrada: boolean; qtd: number }>();
  let soma = 0;
  for (const m of movimentos.filter(contaNoSaldo)) {
    if (m.qtd === 0) continue;
    soma += m.qtd;
    const chave = `${m.tipo}:${m.qtd > 0 ? 'E' : 'S'}`;
    const g = grupos.get(chave) ?? { tipo: m.tipo, entrada: m.qtd > 0, qtd: 0 };
    g.qtd += Math.abs(m.qtd);
    grupos.set(chave, g);
  }
  if (grupos.size === 0) return { texto: null, soma: 0 };
  const partes = [...grupos.values()]
    .sort((a, b) => b.qtd - a.qtd)
    .map((g) => `${un(g.qtd)} de ${g.entrada ? 'entrada' : 'saída'} por ${NOME[g.tipo]}`);
  const lista =
    partes.length === 1
      ? (partes[0] as string)
      : `${partes.slice(0, -1).join(', ')} e ${partes[partes.length - 1] as string}`;
  return { texto: lista, soma };
}

/**
 * Monta o resumo. `inv` = a investigação do caso (razão + saldo ao vivo): `undefined` = ainda
 * buscando; `null` = não veio (falha ou sem acesso) — o resumo segue com o que a fila trouxe.
 */
export function montarResumo(
  c: CasoDaFila,
  inv: Investigacao | null | undefined,
): ResumoDaConferencia {
  const aoVivo = inv?.extrato.saldoAgora ?? null;
  const hosAgora: ResumoDaConferencia['hosAgora'] = aoVivo
    ? { valor: aoVivo.saldo, em: aoVivo.em, fonte: 'AO_VIVO' }
    : c.estoqueAtual !== null
      ? { valor: c.estoqueAtual, em: c.estoqueAtualEm ?? null, fonte: 'ESPELHO' }
      : { valor: null, em: null, fonte: null };

  const fonteFoto: FonteDaFoto | null =
    c.estoqueHosAbertura === null ? 'SEM_LEITURA' : (c.estoqueHosAberturaFonte ?? null);
  const hosNaContagem: ResumoDaConferencia['hosNaContagem'] = {
    valor: c.estoqueHosAbertura,
    em: c.estoqueHosAbertura === null ? null : (c.estoqueHosAberturaEm ?? null),
    fonte: fonteFoto,
  };

  let mudancaHos: ResumoDaConferencia['mudancaHos'] = null;
  if (hosAgora.valor !== null && hosNaContagem.valor !== null) {
    const delta = hosAgora.valor - hosNaContagem.valor;
    if (delta !== 0) {
      const quando = fonteFoto === 'GERACAO' ? 'na geração do Giro' : 'durante a contagem';
      const frase = `O saldo no HOS era ${String(hosNaContagem.valor)} ${quando} e agora é ${String(hosAgora.valor)}.`;
      let explicacao: string | null;
      if (inv === undefined) explicacao = null;
      else if (inv && inv.extrato.fonte === 'HOS_RAZAO' && fonteFoto === 'CONTAGEM') {
        const { texto, soma } = explicarMovimentos(inv.extrato.movimentos);
        if (!texto)
          explicacao =
            'Nenhuma movimentação apareceu no HOS depois da contagem. Consulte as movimentações para verificar o motivo.';
        else {
          explicacao = `Após a contagem, o HOS registrou ${texto}.`;
          if (soma !== delta)
            explicacao += ` Essas movimentações somam ${comSinalUn(soma)}; o restante (${comSinalUn(delta - soma)}) não aparece no razão.`;
        }
      } else explicacao = 'O saldo HOS mudou. Consulte as movimentações para verificar o motivo.';
      mudancaHos = { delta, frase, explicacao };
    }
  }

  const conc = inv?.conciliacao;
  const fisicoTotal =
    conc &&
    conc.fisico !== null &&
    conc.diferenca !== null &&
    (conc.confianca === 'CONFIAVEL' || conc.confianca === 'APROXIMADA')
      ? conc.frase
      : null;

  return {
    hosAgora,
    hosNaContagem,
    prateleira: { valor: c.prateleira, por: c.autoria, em: c.contadoEm ?? null },
    estoque: { valor: null },
    diferenca: diferencaDoCaso(c),
    fisicoTotal,
    mudancaHos,
  };
}

/** A frase da confirmação: a reconferência ao lado do que o balconista tinha declarado. */
export function fraseDaReconferencia(balconista: number | null, encontrado: number): string {
  if (balconista === null) return `Sua reconferência encontrou ${un(encontrado)}.`;
  if (balconista === encontrado) return `Igual ao balconista: ${un(encontrado)} na prateleira.`;
  return `O balconista informou ${un(balconista)} na prateleira. Sua reconferência encontrou ${String(encontrado)}.`;
}
